import { useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useConsentCookie } from "@/components/consent/cookie-consent";
import { usePathname } from "@shared/client/navigation";
import { SESSION_KEY, useSession } from "@shared/client/session";
import { Button } from "@shared/components/ui/button";
import { Popover, PopoverAnchor, PopoverArrow, PopoverContent } from "@shared/components/ui/popover";
import { updateProfile } from "@shared/lib/auth-client";
import type { SessionInfo } from "@shared/lib/auth-shared";
import { GA_MEASUREMENT_ID, parseConsent } from "@shared/lib/consent";
import { useT } from "@shared/client/locale";
import {
  bilanNudge,
  pick,
  TOUR_LAST_ACTION,
  TOUR_STEPS,
  type TourVariant,
} from "./tour-steps";
import { TourWelcome } from "./tour-welcome";

/**
 * Visite guidée de la première session. Elle part seule, une fois par compte
 * (`product_tour_pending`, posé par l'onboarding), quand la carte de veille
 * montre le premier passage ou son bilan. Un voile léger éclaire la cible et
 * fait ressortir la bulle ; il ne bloque rien : la page reste vivante dessous,
 * le radar continue de tourner.
 */

/** Garde locale : couvre un PATCH perdu ou une session relue avant son écriture. */
const DONE_KEY = "hune-tour-done";
const DESKTOP_QUERY = "(min-width: 768px)";
/** La page se pose (vue chargée, éventuel aller-retour de l'onboarding) avant l'accueil. */
const SETTLE_MS = 800;

type TourState =
  | { stage: "idle" }
  | { stage: "welcome"; variant: TourVariant }
  | { stage: "step"; index: number; variant: TourVariant }
  | { stage: "ended"; nudge: boolean };

function subscribeDom(callback: () => void) {
  const observer = new MutationObserver(callback);
  observer.observe(document.body, { childList: true, subtree: true });
  return () => observer.disconnect();
}

function firstMatch(selectors: string[]) {
  for (const selector of selectors) {
    const element = document.querySelector<HTMLElement>(selector);
    if (element) return element;
  }
  return null;
}

/** Le premier élément présent parmi `selectors`, suivi au fil des changements du DOM. */
function useElement(selectors: string[]) {
  const key = selectors.join("\n");
  return useSyncExternalStore(
    subscribeDom,
    () => (key ? firstMatch(key.split("\n")) : null),
    () => null,
  );
}

function subscribeDesktop(callback: () => void) {
  const query = window.matchMedia(DESKTOP_QUERY);
  query.addEventListener("change", callback);
  return () => query.removeEventListener("change", callback);
}

function useDesktop() {
  return useSyncExternalStore(subscribeDesktop, () => window.matchMedia(DESKTOP_QUERY).matches, () => true);
}

function readDone(userId: string) {
  try {
    return window.localStorage.getItem(DONE_KEY) === userId;
  } catch {
    return false;
  }
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Amène la cible à l'écran si la topbar, la barre du bas ou le bord la cachent en partie. */
function bringIntoView(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  const visible = rect.top >= 70 && rect.bottom <= window.innerHeight - 90;
  if (!visible) element.scrollIntoView({ block: "center", behavior: prefersReducedMotion() ? "auto" : "smooth" });
}

export function ProductTour({ disabled, runId = null }: {
  disabled: boolean;
  /** Passage en cours du compte : l'accueil compte ses pages lues. */
  runId?: string | null;
}) {
  const session = useSession();
  const userId = session?.user.id ?? "";
  const pending = session?.user.user_metadata.product_tour_pending === true;
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const desktop = useDesktop();
  const consent = useConsentCookie();
  const t = useT();
  const [doneHere] = useState(() => (userId ? readDone(userId) : false));
  const [state, setState] = useState<TourState>({ stage: "idle" });

  const running = useElement(['[data-tour="agent-steps"]']);
  const settled = useElement(['[data-tour="pass-summary"]']);
  const freshBilan = useElement(['[data-tour="pass-summary"][data-fresh]']);

  // Le bandeau cookies (fixé en bas) passerait sous les bulles de la barre mobile.
  const bannerPending = Boolean(GA_MEASUREMENT_ID) && consent !== null && !parseConsent(consent);
  const onDashboard = pathname === "/app" && !disabled;
  const eligible =
    state.stage === "idle" && pending && !doneHere && onDashboard && !bannerPending && Boolean(running || settled);

  useEffect(() => {
    if (!eligible) return;
    const timer = setTimeout(() => {
      const variant: TourVariant = document.querySelector('[data-tour="agent-steps"]') ? "running" : "settled";
      setState({ stage: "welcome", variant });
    }, SETTLE_MS);
    return () => clearTimeout(timer);
  }, [eligible]);

  /** Fin de visite, terminée ou passée : elle ne revient plus, sur aucun appareil. */
  function end(variant: TourVariant) {
    setState({ stage: "ended", nudge: variant === "running" });
    queryClient.setQueryData<SessionInfo | null>(SESSION_KEY, (current) =>
      current
        ? { ...current, user: { ...current.user, user_metadata: { ...current.user.user_metadata, product_tour_pending: false } } }
        : current,
    );
    updateProfile({ product_tour_pending: false }).catch(() => {});
    try {
      window.localStorage.setItem(DONE_KEY, userId);
    } catch {
      // Stockage indisponible (navigation privée) : le profil suffit.
    }
  }

  function finish(variant: TourVariant) {
    end(variant);
    const target = document.querySelector<HTMLElement>(TOUR_LAST_ACTION[variant].focus);
    if (target) {
      bringIntoView(target);
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    }
  }

  if (state.stage === "welcome") {
    const variant = state.variant;
    return (
      <TourWelcome
        onSkip={() => end(variant)}
        onStart={() => setState({ stage: "step", index: 0, variant })}
        runId={runId}
        variant={variant}
      />
    );
  }

  if (state.stage === "step") {
    if (!onDashboard) return null; // en pause : la visite reprend au retour sur la vue d'ensemble
    const step = TOUR_STEPS[state.index];
    const copy = t.shell.tour.steps[step.id];
    const variant = state.variant;
    const last = state.index === TOUR_STEPS.length - 1;
    const anchors = pick(step.anchors, variant);
    return (
      <>
        <Spotlight anchors={anchors} />
        <TourBubble
          align={step.align}
          anchors={anchors}
          body={pick<string>(copy.body, variant)}
          counter={t.shell.tour.counter(state.index + 1, TOUR_STEPS.length)}
          desktop={desktop}
          key={step.id}
          nextLabel={last ? t.shell.tour.lastAction[variant] : t.shell.tour.next}
          onNext={() => (last ? finish(variant) : setState({ stage: "step", index: state.index + 1, variant }))}
          onSkip={last ? undefined : () => end(variant)}
          side={desktop ? step.side.desktop : step.side.mobile}
          title={pick<string>(copy.title, variant)}
        />
      </>
    );
  }

  if (state.stage === "ended" && state.nudge && freshBilan && onDashboard) {
    const nudge = bilanNudge(Number(freshBilan.dataset.newLeads ?? 0), t.shell.tour.nudge);
    return (
      <>
        <Spotlight anchors={nudge.anchors} />
        <TourBubble
          align={nudge.align}
          anchors={nudge.anchors}
          body={nudge.body}
          desktop={desktop}
          onClose={() => setState({ stage: "ended", nudge: false })}
          side="bottom"
          title={t.shell.tour.nudge.title}
        />
      </>
    );
  }

  return null;
}

function TourBubble({
  anchors,
  side,
  align,
  desktop,
  title,
  body,
  counter,
  nextLabel,
  onNext,
  onSkip,
  onClose,
}: {
  anchors: string[];
  side: "top" | "right" | "bottom" | "left";
  align: "start" | "center" | "end";
  desktop: boolean;
  title: string;
  body: string;
  counter?: string;
  nextLabel?: string;
  onNext?: () => void;
  onSkip?: () => void;
  onClose?: () => void;
}) {
  const target = useElement(anchors);
  const t = useT();
  const anchorRef = useMemo(() => ({ current: target }), [target]);
  const id = useId();

  useEffect(() => {
    if (target) bringIntoView(target);
  }, [target]);

  if (!target) return null;

  return (
    <Popover open>
      <PopoverAnchor virtualRef={anchorRef as React.RefObject<HTMLElement>} />
      <PopoverContent
        align={align}
        aria-describedby={`${id}-body${counter ? ` ${id}-counter` : ""}`}
        aria-labelledby={`${id}-title`}
        className="z-[51] w-[min(20rem,calc(100vw-2rem))] rounded-lg p-4 duration-150 data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100"
        collisionPadding={desktop ? 16 : { top: 66, bottom: 80, left: 16, right: 16 }}
        data-tour-bubble=""
        onCloseAutoFocus={(event) => event.preventDefault()}
        onEscapeKeyDown={() => (onSkip ?? onClose)?.()}
        onFocusOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => {
          // Le focus entre dans la bulle (Tab atteint ses boutons, Échap la ferme)
          // sans qu'un anneau d'ardoise ne s'allume sur un bouton qu'on n'a pas visé.
          event.preventDefault();
          (event.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
        }}
        side={side}
        sideOffset={10}
      >
        <PopoverArrow />
        <div className="flex items-start gap-3">
          <p className="m-0 mr-auto text-[14px] leading-snug font-semibold" id={`${id}-title`}>
            {title}
          </p>
          {onClose && (
            <Button
              aria-label={t.shell.tour.close}
              className="-mt-1.5 -mr-1.5 size-7 text-muted-foreground"
              onClick={onClose}
              size="icon-sm"
              variant="ghost"
            >
              <X />
            </Button>
          )}
        </div>
        <p className="mt-1.5 mb-0 text-[13px] leading-relaxed text-muted-foreground" id={`${id}-body`}>
          {body}
        </p>
        {(counter || onNext) && (
          <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
            {counter && (
              <span className="mr-auto text-[12px] text-muted-foreground tabular-nums" id={`${id}-counter`}>
                {counter}
              </span>
            )}
            {onSkip && (
              <Button className="text-muted-foreground max-md:h-11" onClick={onSkip} size="sm" variant="ghost">
                {t.shell.tour.skip}
              </Button>
            )}
            {onNext && nextLabel && (
              <Button className="max-md:h-11" onClick={onNext} size="sm" variant="outline">
                {nextLabel}
              </Button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Voile gris léger sur toute la page, percé autour de la cible : la bulle
 * blanche et l'élément commenté ressortent. Le trou suit la cible à chaque
 * image (défilement, mise en page) et glisse d'une étape à l'autre, le voile
 * restant monté. Il laisse passer les clics : la visite ne bloque jamais la page.
 */
function Spotlight({ anchors }: { anchors: string[] }) {
  const target = useElement(anchors);
  const holeRef = useRef<HTMLDivElement>(null);
  const placedRef = useRef(false);

  useEffect(() => {
    if (!target) return;
    // Une icône du rail s'éclaire à la taille de son entrée ; le reste, au plus près.
    const pad = target instanceof SVGElement ? 10 : 6;
    let frame = 0;
    const follow = () => {
      const hole = holeRef.current;
      if (hole) {
        const rect = target.getBoundingClientRect();
        // Première pose sans glissement : le trou n'arrive pas du coin de l'écran.
        hole.style.transition = placedRef.current ? "" : "none";
        hole.style.transform = `translate(${rect.left - pad}px, ${rect.top - pad}px)`;
        hole.style.width = `${rect.width + pad * 2}px`;
        hole.style.height = `${rect.height + pad * 2}px`;
        placedRef.current = true;
      }
      frame = requestAnimationFrame(follow);
    };
    follow();
    return () => cancelAnimationFrame(frame);
  }, [target]);

  return createPortal(
    <div
      aria-hidden="true"
      className="pointer-events-none fixed top-0 left-0 z-50 rounded-[10px] shadow-[0_0_0_200vmax_color-mix(in_srgb,var(--foreground)_22%,transparent)] transition-[transform,width,height] duration-200 ease-out animate-in fade-in-0"
      data-tour-spotlight=""
      ref={holeRef}
    />,
    document.body,
  );
}
